/* eslint-disable @angular-eslint/no-input-rename */
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  NgZone,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, Event } from '@angular/router';
import { configureScope } from '@sentry/browser';
import {
  EMPTY,
  Subscription,
  Observable,
  Subject,
  throwError,
  catchError,
  switchMap,
  takeUntil,
} from 'rxjs';
import { UsersAPIService } from '@app/akita/api/users/services/user-api.service';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import {
  Validators,
  ValidationErrors,
  UntypedFormControl,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { HttpStatusCode } from '@app/shared/utils/http-status-codes';
import { AkitaAuthService } from '@app/akita/api/auth/state/auth.service';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { UserModel } from '@app/shared/models/api/user.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { CryptoUtils } from '@app/shared/utils/crypto.util';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { NewPasswordIconComponent } from '../../../shared/components/icons/generated/new-password/new-password.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { View1IconComponent } from '../../../shared/components/icons/generated/view-1/view-1.component';
import { ViewOffIconComponent } from '../../../shared/components/icons/generated/view-off/view-off.component';
import { NgIf, AsyncPipe } from '@angular/common';

const MIN_PASSWORD_LENGTH = 6;

@Component({
  selector: 'app-reset-password-change',
  templateUrl: './reset-password-change.template.html',
  styleUrls: ['./reset-password-change.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    ReactiveFormsModule,
    FormsModule,
    ViewOffIconComponent,
    View1IconComponent,
    LoadingComponent,
    NewPasswordIconComponent,
    AsyncPipe,
  ],
})
export class ResetPasswordChangeComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public inDialog?: boolean | null = null;
  @Input('email') public placeholderEmail?: string | null = null;
  @Input('username') public placeholderUsername?: string | null = null;
  @Input('previous-mode') public previousMode?: string | null = null;
  @Input('skip-redirect') public skipRedirect?: boolean | null = null;

  @Output() public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();

  private readonly ANDROID_DEEP_LINK_URI: string =
    'https://bxh6n.app.goo.gl/?apn=com.mypopsy.android';

  public readonly passwordFormControl: UntypedFormControl;
  public redirect?: string | null = null;
  public user: UserModel | null = null;
  public hidePassword: boolean;
  public token: string | null = null;
  public submitted: boolean;
  public failed: boolean;
  public success: boolean;
  public loading: boolean;
  public isBrowser: boolean;

  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};

  private readonly subscriptions: Subscription;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout$: Observable<boolean>;

  private readonly destroySubject: Subject<void>;

  @ViewChild('password') private readonly passwordElement?: ElementRef | null;

  constructor(
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly usersApiService: UsersAPIService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaAuthService: AkitaAuthService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.hidePassword = true;
    this.submitted = false;
    this.loading = false;
    this.success = false;
    this.failed = false;
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();

    this.redirect = null;
    this.user = null;
    this.token = null;

    this.passwordFormControl = new UntypedFormControl('', {
      validators: Validators.compose([
        Validators.required,
        Validators.minLength(MIN_PASSWORD_LENGTH),
      ]),
      updateOn: 'change',
    });

    this.subscriptions.add(
      this.translateService.langChanges$.pipe(takeUntil(this.destroySubject)).subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_login', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      })
    );

    // Listen to Navigation End events
    this.subscriptions.add(
      this.router.events.pipe(takeUntil(this.destroySubject)).subscribe({
        next: ($event: Event) => {
          if ($event instanceof NavigationEnd) {
            let route = this.activatedRoute.snapshot;
            while (route.firstChild) {
              route = route.firstChild;
            }
            this.onNavigationEnd(
              route.params || {},
              route.queryParams || {},
              route.data || {}
            );
          }
        },
      })
    );
  }

  /**
   * Method called when the component is initialized
   */
  public ngOnInit(): void {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.onNavigationEnd(route.params || {}, route.queryParams || {}, route.data || {});
  }

  public ngAfterViewInit(): void {
    // Set Focus
    if (this.isBrowser) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          if (this.passwordElement?.nativeElement?.focus) {
            this.passwordElement.nativeElement.focus();
          }
        }, 0);
      });
    }
  }

  /**
   * Method called when the view loads
   * @param params: URL Params
   * @param queryParams: URL Query Params
   * @param routeData: Route Data
   */
  public onNavigationEnd(params: Params, queryParams: Params, routeData?: Params): void {
    this.params = params || {};
    this.queryParams = queryParams || {};
    this.routeData = routeData || {};
    this.token = this.queryParams.token || null;
    this.redirect = decodeURI(this.queryParams.redirect || this.params.redirect || '');
    this.changeDetector.markForCheck();
  }

  /**
   * Method called when the Sign in (Popsy or Facebook) completes
   * @param user The user instance from the Backend
   */
  public loggedIn(): boolean {
    this.loading = false;
    if (this.user) {
      this.success = true;
      this.akitaAuthService.userSignedIn(this.user);
    } else {
      this.success = false;
    }
    this.changeDetector.markForCheck();

    this.handleRedirect.next();
    if (!this.skipRedirect) {
      if (this.redirect && this.user && this.user.oauth && this.user.oauth.token) {
        WindowUtils.setLocationHref(
          `${this.redirect}${this.user.oauth.token.accessToken}`
        );
        return false;
      } else {
        // let params = {};
        // if (this.redirect) {
        //   params = { ...params, redirect: encodeURI(this.redirect) };
        // }

        // this.router.navigate(this.homeUrlFragment, {
        //   queryParams: params
        // });

        // TODO: Remove Android Hack & uncomment Code from avove
        this.zone.runOutsideAngular(() => {
          window.setTimeout(() => {
            const originLink = `${WindowUtils.getLocationOrigin()}/?token=${this.token}`;
            WindowUtils.setLocationHref(
              `${this.ANDROID_DEEP_LINK_URI}&link=${originLink}`
            );
          }, 0);
        });
      }
    }

    this.close(false);
    return true;
  }

  /**
   * Sign in with Popsy
   */
  public resetPassword(): void {
    if (this.passwordFormControl?.status !== 'VALID') {
      return;
    }

    this.subscriptions.add(
      CryptoUtils.Sha256(this.passwordFieldValue)
        .pipe(
          switchMap((password: string): Observable<UserModel | null> => {
            this.loading = true;
            this.submitted = true;
            this.failed = false;
            this.success = false;

            configureScope((scope) => {
              scope.setUser({
                id: '?',
                username: '',
                email: this.token || '',
              });
            });

            if (this.token) {
              return this.usersApiService.resetPassword(this.token, password).pipe(
                catchError((error: unknown) => {
                  const parsed = error as HttpErrorResponse;
                  this.failed = true;
                  this.loading = false;

                  configureScope((scope) => {
                    scope.setUser({});
                  });

                  let errorCode = '';
                  if (parsed?.error?.error) {
                    errorCode = parsed.error.error;
                  }

                  if (error && parsed.status === HttpStatusCode.UNAUTHORIZED) {
                    this.setPasswordFieldError({ UNAUTHORIZED: true });
                  } else if (errorCode === 'USER_NOT_FOUND') {
                    this.setPasswordFieldError({ USER_NOT_FOUND: true });
                  } else if (errorCode === 'USER_BANNED') {
                    this.setPasswordFieldError({ USER_BANNED: true });
                  } else {
                    this.setPasswordFieldError({ UNKNOWN: true });
                  }

                  this.changeDetector.markForCheck();
                  return EMPTY;
                }),
                takeUntil(this.destroySubject)
              );
            } else {
              this.setPasswordFieldError({ UNAUTHORIZED: true });
              this.changeDetector.markForCheck();
              return throwError(() => parseApiError('UNAUTHORIZED'));
            }
          }),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: (user: UserModel | null) => {
            this.user = user || null;
            this.submitted = true;
            this.success = true;
            this.changeDetector.markForCheck();
          },
          error: () => {
            this.setPasswordFieldError({ UNKNOWN: true });
          },
        })
    );
  }
  public get signUpQueryParams(): any {
    let params = {};
    const email =
      this.params.email || this.queryParams.email || this.placeholderEmail || '';
    if (email) {
      params = { ...params, email: email };
    }
    const username = this.params.username || this.queryParams.username || '';
    if (username) {
      params = { ...params, username: username };
    }
    if (this.redirect) {
      params = { ...params, redirect: encodeURI(this.redirect) };
    }
    return params;
  }

  public stopPropagation(): boolean {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public togglePassword($event: any): boolean {
    this.hidePassword = !this.hidePassword;
    this.changeDetector.markForCheck();
    $event.preventDefault();
    return false;
  }

  public close(dismiss?: boolean | null): boolean {
    this.closeDialog.emit(Boolean(dismiss));
    return false;
  }

  public get passwordFieldValue(): string {
    let value = '';
    if (this.passwordFormControl) {
      value = this.passwordFormControl.value;
    }
    return value;
  }

  public setPasswordFieldError(errors: ValidationErrors | null): void {
    if (this.passwordFormControl) {
      this.passwordFormControl.setErrors(errors);
      this.passwordFormControl.markAsTouched();
    }
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
